import React, { useState, useEffect, useRef, useMemo } from 'react';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import {
    MultilineTextBox,
    DropDownBox,
    IconButton,
    IconButtonTheme,
    IconButtonSize,
    FileChooser,
    GenericAlertPopup,
} from '@apply/components';
import '../../scss/_fileOrFormQuestion.scss';
import { CustomValidationMessage } from '../CustomValidationMessage';
import { AttachmentType } from './types/AttachmentType';
import { Attachment } from './types/Attachment';
import { AttachmentHandleMode } from './types/AttachmentHandleMode';
import { formAction } from './constants/FormAction';
import { ApplicationQuestionReviewItem } from '../ApplicationQuestionReviewItem';
import { ApplyApi } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../../../ApplicationInsights';
import { logApplicationInsightsError } from '../../../common/functions/applicationInsightFunctions';
import { PasteEvent } from 'devextreme/ui/text_box';
import { CustomRule, Validator } from 'devextreme-react/validator';
import { useParams } from 'react-router-dom';
import { useBorrowerLoanType } from '../../../queries/BorrowerFields/CoreFields';

export interface FileOrFormQuestionProps {
    label: string;
    attachment?: Attachment;
    formTypeName?: string;
    selector: string;
    fileType?: AttachmentType;
    propertyGuid?: string;
    disableEditFormOption?: boolean;
    required: boolean;
    maxWidth?: string;
    isReview?: boolean;
    // Text data (used for general contractor resume 'Enter Text' mode)
    data?: string;
    set_data?: (value: string) => void;
    // Attachment state — provided by wrapper components via usePropertyField
    handle: AttachmentHandleMode;
    onHandleChange: (value: AttachmentHandleMode) => void;
    uploadFileName?: string;
    onUploadFileNameChange: (value: string | undefined) => void;
    uploadedFileApiId?: string;
    onUploadedFileApiIdChange: (value: string | undefined) => void;
}

type ExtendedPasteEvent = PasteEvent & { event: { originalEvent: { clipboardData: { getData: (text: string) => string } } } };

export const FileOrFormQuestion: React.FC<FileOrFormQuestionProps> = ({
    label,
    selector,
    fileType,
    formTypeName,
    propertyGuid,
    disableEditFormOption,
    required,
    maxWidth = 'auto',
    isReview = false,
    handle,
    onHandleChange,
    uploadFileName,
    onUploadFileNameChange,
    uploadedFileApiId,
    onUploadedFileApiIdChange,
    data,
    set_data,
}) => {
    const applyApi = new ApplyApi(applicationInsights);

    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const [loanType] = useBorrowerLoanType();

    const [showValidationMessage, setShowValidationMessage] = useState<boolean>(false);
    const [passing, setPassing] = useState<boolean>(false);
    const [touched, setTouched] = useState<boolean>(false);

    const [uploading, setUploading] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string>('');

    const isValidValue = (): boolean => {
        if (!handle) {
            return false;
        }

        if (handle === formAction.upload) {
            return !!uploadedFileApiId;
        }

        if (handle === 'unset') return false;

        if (handle === 'textdata') {
            if (data?.trim()?.length == undefined || data?.trim()?.length < 30) {
                return false;
            }
        }

        return true;
    };

    const validationMessage = useMemo(() => {
        if (handle === formAction.upload) {
            return 'Upload required';
        }
        if (handle === formAction.textData && data && data?.trim()?.length < 30) {
            return 'Please provide a little more information';
        }
        return;
    }, [handle, data]);

    const firstUpdate = useRef(true);

    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }
        if (handle !== 'textdata' && !!data) {
            if (set_data) set_data('');
            return;
        }
        setPassing(isValidValue());
    }, [handle, data]);

    useEffect(() => {
        setShowValidationMessage(!passing);
    }, [passing, selector]);

    const handleModeChange = (value: string) => {
        setTouched(true);
        onHandleChange(value as AttachmentHandleMode);
    };

    const handleTextDataChange = (value: string) => {
        if (set_data) set_data(value);
    };

    const onRemoveFile = () => {
        // TODO: We should call to the server and delete the uploaded file...
        onHandleChange(formAction.upload);
        onUploadFileNameChange(undefined);
        onUploadedFileApiIdChange(undefined);

        setPassing(false);
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setUploading(true);
        try {
            if (!loanType) return;
            if (!fileType) return;

            if ((e?.target?.files?.length ?? 0 > 0) && e.target.files != null) {
                const response = await applyApi.saveApplicationFile({
                    file: e.target.files[0],
                    fileType,
                    loanType,
                    entryGuid: entryGuid ?? '',
                    propertyGuid,
                });

                if (response.statusCode !== 200) {
                    logApplicationInsightsError(response.errors[0]);
                    return;
                }

                onUploadFileNameChange(response.result.safeFileName);
                onUploadedFileApiIdChange(response.result.applicationFileId);
            }
        } catch (err) {
            setUploadError((err as Error).message);
        }
        setUploading(false);
        setTouched(true);
        setPassing(true);
    };

    useEffect(() => {
        if (uploadError) {
            // todo this could be used to display feedback to user like try again or somesuch
            logApplicationInsightsError(new Error(uploadError));
        }
    }, [uploadError]);

    const activeSelectOptions = [
        //Initial Unset Option
        { value: undefined, description: '', disabled: false },

        //FormTypeName provided
        ...(formTypeName ? [{ value: formAction.create, description: `Create New ${formTypeName}`, disabled: false }] : []),
        ...(formTypeName
            ? [{ value: formAction.edit, description: `Saved ${formTypeName} (editable)`, disabled: disableEditFormOption }]
            : []),

        //Upload File
        { value: formAction.upload, description: 'Upload File', disabled: false },

        //Text Data
        ...(fileType === 'general contractor resume'
            ? [{ value: formAction.textData, description: 'Enter Text', disabled: false }]
            : []),

        //Provide Later
        { value: formAction.provideLater, description: 'Provide Later', disabled: false },

        //Will Not Provide
        ...(fileType === 'spec sheet'
            ? [{ value: formAction.willNotProvide, description: 'Will Not Provide', disabled: false }]
            : []),
    ];

    const reviewModeValue = () => {
        switch (handle) {
            case formAction.provideLater:
                return 'to be provided separately';
            case formAction.willNotProvide:
                return 'will not provide';
            case formAction.edit:
            case formAction.create:
                return 'form filled online';
            case formAction.upload:
                return 'file uploaded';
            case formAction.textData:
                return data;
            default:
                return '';
        }
    };

    const handleClipboardEvent = (e: ExtendedPasteEvent) => {
        //Devextreme has a bug in their pasteevent - clipboardData accessible via original event (not in documentation)
        const newData = e?.event?.originalEvent?.clipboardData?.getData('text') ?? '';
        const existingLength = data?.length ?? 0;
        if (existingLength + newData.length < 2000) {
            return;
        }

        e.event?.preventDefault();
        const truncated = newData.substring(0, 2000 - existingLength - 2);
        if (set_data) set_data((data ?? '') + truncated);
        GenericAlertPopup({
            children: 'The character limit is 2000 characters, some text has been cut off.',
        });
    };

    return (
        <>
            {isReview ? (
                <ApplicationQuestionReviewItem
                    label={label}
                    value={reviewModeValue()}
                />
            ) : (
                <>
                    <div
                        className='fileorform-question'
                        style={{ maxWidth: maxWidth }}
                    >
                        {!uploadedFileApiId && (
                            <div className='fileorform-question-dropdown'>
                                <DropDownBox
                                    id={`fileorform-handlemode-${selector}`}
                                    width='300px'
                                    value={handle}
                                    label={label}
                                    onChange={handleModeChange}
                                    dataSource={activeSelectOptions}
                                    valueExpr='value'
                                    displayExpr='description'
                                    required={required}
                                    validator={
                                        required ? (
                                            <CustomRule
                                                validationCallback={({ value }: { value: unknown }) =>
                                                    !!value && value !== 'unset'
                                                }
                                                message='This field is required.'
                                            />
                                        ) : undefined
                                    }
                                />
                            </div>
                        )}

                        <div className='fileorform-question-fileselector'>
                            {!uploadedFileApiId && handle === formAction.upload && (
                                <>
                                    <FileChooser
                                        id={`fileorform-file-${selector}`}
                                        dataTestid={`fileorform-file-${selector}`}
                                        handleChange={handleFileSelect}
                                        labeledBy={`fileorform-label-${selector}`}
                                        required={required}
                                    />
                                    {required && (
                                        <Validator
                                            adapter={{
                                                getValue: () => uploadedFileApiId,
                                                applyValidationResults: () => {},
                                            }}
                                        >
                                            <CustomRule
                                                reevaluate={true}
                                                validationCallback={({ value }: { value: unknown }) => !!value}
                                                message=''
                                            />
                                        </Validator>
                                    )}
                                </>
                            )}
                            {handle === formAction.upload && !!uploadedFileApiId && (
                                <div className='fileorform-question-selectedFileWrapper textbox-container'>
                                    <label className={'textbox-label'}>{label}</label>{' '}
                                    <div className='fileorform-question-selectedFile'>
                                        <div style={{ paddingTop: '10px' }}>{uploadFileName}</div>
                                        <IconButton
                                            id={`fileorform-file-${selector}-deleteButton`}
                                            handleClick={onRemoveFile}
                                            theme={IconButtonTheme.TransparentRed}
                                            size={IconButtonSize.Small}
                                            hint='Remove file'
                                            icon={faTrashCan}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {handle === 'textdata' && (
                        <div
                            className='fileorform-question-textbox'
                            style={{ maxWidth: maxWidth }}
                        >
                            <MultilineTextBox
                                maxLength={2000}
                                value={data}
                                onChange={handleTextDataChange}
                                onPaste={(e) => handleClipboardEvent(e as ExtendedPasteEvent)}
                                placeholder={`Please provide ${label} text...`}
                                height='150px'
                                width='300px'
                            />
                        </div>
                    )}
                    {touched && showValidationMessage && (
                        <CustomValidationMessage>{validationMessage}</CustomValidationMessage>
                    )}
                    {/* </fieldset> */}
                    <div
                        data-testid={`file-upload-overlay-${selector}`}
                        className='file-upload-overlay'
                        style={{ display: uploading ? 'block' : 'none' }}
                    >
                        <progress></progress>
                    </div>
                </>
            )}
        </>
    );
};
